<script setup>
import CustomerUserTable from '@frontJs/components/tables/CustomerUserTable.vue';
import CustomerWithoutUser from '@frontJs/components/tables/CustomerWithoutUser.vue';
import UserTable from '@frontJs/components/tables/UserTable.vue';
import DefaultLayout from '@frontJs/layouts/DefaultLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { useRoute } from '@js/utils/route';
import { h, onMounted, ref } from 'vue';

const { route } = useRoute();

const loading = ref(true);

const data = ref({
    superAdmin: 0,
    archivedMultiAccounts: 0,
    activeMultiAccounts: 0,
    archivedCustomers: 0,
    activeCustomers: 0,
    inactiveCustomers: 0,
    activeCustomersWithoutAccount: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(route('front.postUserCount'), {
            role: 'super_admin',
        }),
        axios.post(route('front.postUserCount'), {
            role: 'admin',
        }),
        axios.post(route('front.postUserCount'), {
            role: 'multi_account',
            archived: true,
        }),
        axios.post(route('front.postUserCount'), {
            role: 'multi_account',
            active: true,
        }),
        axios.post(route('front.postCustomerUserCount'), {
            archived: true,
        }),
        axios.post(route('front.postCustomerUserCount'), {
            archived: false,
            active: true,
        }),
        axios.post(route('front.postCustomerUserCount'), {
            archived: false,
            active: false,
        }),
        axios.post(route('front.postCustomerWithoutUserCount')),
    ])
        .then(
            ([
                superAdminCountData,
                adminCountData,
                archivedMultiAccountsCountData,
                activeMultiAccountsCountData,
                archivedCustomersCountData,
                activeCustomersCountData,
                inactiveCustomersCountData,
                activeCustomersWithoutAccountCountData,
            ]) => {
                data.value.superAdmin = superAdminCountData.data.count;
                data.value.admin = adminCountData.data.count;
                data.value.archivedMultiAccounts =
                    archivedMultiAccountsCountData.data.count;
                data.value.activeMultiAccounts =
                    activeMultiAccountsCountData.data.count;
                data.value.archivedCustomers =
                    archivedCustomersCountData.data.count;
                data.value.activeCustomers =
                    activeCustomersCountData.data.count;
                data.value.inactiveCustomers =
                    inactiveCustomersCountData.data.count;
                data.value.activeCustomersWithoutAccount =
                    activeCustomersWithoutAccountCountData.data.count;
            },
        )
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    updateData();
});

defineOptions({ layout: DefaultLayout });
</script>

<template>
    <Head>
        <title>Utilisateurs - Datakepler</title>
    </Head>

    <PageTitle> Utilisateurs </PageTitle>

    <TabLayout
        :tabs="{
            superAdmin: {
                title: 'Super Admin',
                total: data.superAdmin,
                content: h(UserTable, {
                    role: 'super_admin',
                }),
            },
            admin: {
                title: 'Admin',
                total: data.admin,
                content: h(UserTable, {
                    role: 'admin',
                }),
            },
            archivedMultiAccounts: {
                title: 'Multi-comptes archivés',
                total: data.archivedMultiAccounts,
                content: h(UserTable, {
                    role: 'multi_account',
                    archived: true,
                }),
            },
            activeMultiAccounts: {
                title: 'Multi-comptes actifs',
                total: data.activeMultiAccounts,
                content: h(UserTable, {
                    role: 'multi_account',
                    active: true,
                }),
            },
            archivedCustomers: {
                title: 'Clients archivés',
                total: data.archivedCustomers,
                content: h(CustomerUserTable, {
                    archived: true,
                }),
            },
            activeCustomers: {
                title: 'Clients actifs',
                total: data.activeCustomers,
                content: h(CustomerUserTable, {
                    archived: false,
                    active: true,
                }),
            },
            inactiveCustomers: {
                title: 'Clients inactifs',
                total: data.inactiveCustomers,
                content: h(CustomerUserTable, {
                    archived: false,
                    active: false,
                }),
            },
            activeCustomersWithoutAccount: {
                title: 'Clients actifs sans compte',
                total: data.activeCustomersWithoutAccount,
                content: h(CustomerWithoutUser),
            },
        }"
        :loading="loading"
    />
</template>
